import {
  validators
} from 'ui-validator';
import http from 'lc-http';
import _isEmpty from 'lodash.isempty';
import { post } from '../../lib/http-request';

import {
  submitOwnershipInfo,
  submitOwnershipFlag,
  addOwner,
  updateOwner,
  removeOwner,
  hasIncompleteOwner
} from './ownership';

import {
  setPreviousRoute,
  getApplication,
  UPDATE_PROGRESS_BAR
} from './onboarding';

import {
  updateControllerField,
  updateManagementInfo,
  updateManagementField
} from './significantController';

import {
  submitConfirmation
} from '../../components/review/actions';

import {
  validateStreet,
  validateAptSuite,
  validateCity,
  validateState,
  validateZip
} from '../../components/address/validations';

import {
  enterStreetAddress,
  enterAptSuite,
  enterCity,
  selectState,
  enterZip
} from '../../components/address/actions';

const PRACTICE_NAME_REQUIRED = 'Practice name is required.';
const PRACTICE_EMAIL_REQUIRED = 'Practice email is required.';
const PRACTICE_PHONE_REQUIRED = 'Practice phone number is required.';
const PRACTICE_TYPE_REQUIRED = 'Please select a practice type.';

export const ENTER_PRACTICE_NAME = 'ENTER_PRACTICE_NAME';
export const VALIDATE_PASSWORD = 'VALIDATE_PASSWORD';

export const PROCESSING_SUBMIT = 'PROCESSING_SUBMIT';
export const SUBMIT_COMPLETED = 'SUBMIT_COMPLETED';

const validateEmail = (value) => (typeof value === 'string' ? validators.email({
  message: 'A valid email address is required.'
})(value.trim()) : PRACTICE_EMAIL_REQUIRED);
const validatePhone = (value) => (value ? validators.validTelephone({
  message: 'A valid phone number is required.'
})(value) : PRACTICE_PHONE_REQUIRED);

const validatePasswordMatch = (val, val2) => val !== val2 && 'Your password must match the same password you entered above.';

const validatePassword = (value) => {
  if (!value || !value.match(/^(?=.*\d+)(?=.*[a-zA-Z])[0-9a-zA-Z!@#$%]{8,30}$/)) {
    return 'Password must contain 8 characters with at least one letter and one number; special characters can only include !, @, #, $ or %.';
  }
};

export const validatePasswordField = (val, confirmVal) => {
  const errors = {
    password: validatePassword(val)
  };

  if (confirmVal) {
    errors.passwordConfirm = validatePasswordMatch(val, confirmVal);
  }

  return {
    type: VALIDATE_PASSWORD,
    errors
  };
};

const validateName = (fieldDisplayName) => {
  const condition = /^[0-9a-zA-Z'\-\.,/&\+\(\) ]+$/;
  const message = fieldDisplayName + ' is invalid.';
  const validateFunction = (value) => {
    if (!condition.test(value)) {
      return message;
    }
    return null;
  };
  return validateFunction;
};

export const enterPracticeName = (value, executeValidation = true) => {
  const action = {
    type: ENTER_PRACTICE_NAME,
    value
  };

  if (executeValidation) {
    action.errMsg = !value && PRACTICE_NAME_REQUIRED;
  }

  return action;
};

export const ENTER_PRACTICE_EMAIL = 'ENTER_PRACTICE_EMAIL';
export const enterPracticeEmail = (email, executeValidation = true) => {
  const value = typeof email === 'string' ? email.trim() : email;
  const action = {
    type: ENTER_PRACTICE_EMAIL,
    value
  };

  if (executeValidation) {
    action.errMsg = value
      ? validators.email({
        message: 'A valid email address is required.'
      })(value.trim())
      : 'Practice email is required.';
  }

  return action;
};

export const ENTER_PRACTICE_PHONE = 'ENTER_PRACTICE_PHONE';
export const enterPracticePhone = (value) => {
  if (value) {
    const errMsg = validators.validTelephone({
      message: 'A valid phone number is required.'
    })(value);
    return errMsg ? {
      type: ENTER_PRACTICE_PHONE,
      value,
      errMsg
    } : {
      type: ENTER_PRACTICE_PHONE,
      value
    };
  }
  return {
    type: ENTER_PRACTICE_PHONE,
    errMsg: 'Practice phone number is required.'
  };
};

export const SELECT_PRACTICE_TYPE = 'SELECT_PRACTICE_TYPE';
export const selectPracticeType = (value) => {
  if (value) {
    return {
      type: SELECT_PRACTICE_TYPE,
      value,
      errMsg: null
    };
  }
  return {
    type: SELECT_PRACTICE_TYPE,
    errMsg: 'Practice type is required.'
  };
};

const submitPracticeData = (data, requestedMoreInfo) => {
  const query = requestedMoreInfo
    ? '?infoRequest=true'
    : '?infoRequest=false';
  data.variation = 'ALT';

  return post(`/patientsolutions/enroll/submit${query}`, data);
};

export const SELECT_HOW_DID_YOU_HEAR_ABOUT_US = 'SELECT_HOW_DID_YOU_HEAR_ABOUT_US';
const HOW_DID_YOU_HEAR_ABOUT_US_REQUIRED = 'Please tell us how did you hear about us.';
const validateHowDidYouHearAboutUs = (value, defaultValue = 'Select') => {
  let errMsg;
  if (!value) {
    errMsg = HOW_DID_YOU_HEAR_ABOUT_US_REQUIRED;
  }
  return errMsg;
};

export const selectHowDidYouHearAboutUs = (value, defaultValue, executeValidation = true) => {
  const action = {
    type: SELECT_HOW_DID_YOU_HEAR_ABOUT_US,
    value
  };
  if (executeValidation) {
    action.errMsg = validateHowDidYouHearAboutUs(value, defaultValue);
  }
  return action;
};

const submitContacts = (data) => {
  data.variation = 'ALT';
  return post('/patientsolutions/enroll/contact-information', data);
};

const registerAccount = (data) => {
  data.variation = 'ALT';
  return post('/patientsolutions/enroll/create-account/action', data);
};

export const SUBMIT_PRACTICE_INFO = 'SUBMIT_PRACTICE_INFO';
export const SUBMIT_PRACTICE_INFO_COMPLETED = 'SUBMIT_PRACTICE_INFO_COMPLETED';

export const submitPracticeInfo = (data, requestedMoreInfo, submitHandler = submitPracticeData) => {
  return (dispatch) => {
    return new Promise((resolve, reject) => {
      if (data) {
        const errors = {};

        if (!data.name) {
          errors.practiceName = PRACTICE_NAME_REQUIRED;
        }

        const emailMsg = validateEmail(data.email);
        if (emailMsg) {
          errors.practiceEmail = emailMsg;
        }

        const phoneMsg = validatePhone(data.phone);
        if (phoneMsg) {
          errors.practicePhone = phoneMsg;
        }

        if (!data.type) {
          errors.practiceType = PRACTICE_TYPE_REQUIRED;
        }

        const howDidYouHearAboutUsMsg = validateHowDidYouHearAboutUs(data.howDidYouHearAboutUs);
        if (howDidYouHearAboutUsMsg) {
          errors.howDidYouHearAboutUs = howDidYouHearAboutUsMsg;
        } else {
          if (data.showAdditionalInfoInput && !data.additionalInfo) {
            errors.additionalInfo = 'Additional Information is required.';
          }
        }

        const hasErrors = Object.keys(errors)
          .reduce((curr, key) => (errors[key] || curr), false);

        if (hasErrors) {
          dispatch({
            type: SUBMIT_PRACTICE_INFO,
            errors
          });

          return reject({
            hasErrors: true
          });
        }

        dispatch({
          type: PROCESSING_SUBMIT
        });

        submitHandler(data, requestedMoreInfo)
          .then((data) => {
            // TODO: read percentage from API call
            dispatch({
              type: SUBMIT_PRACTICE_INFO_COMPLETED,
              payload: { ...data }
            });
            resolve(data);
          })
          .catch(({ response }) => {
            const { status, data } = response || {};

            if (status === 400) {
              return dispatch({
                type: SUBMIT_PRACTICE_INFO,
                errors: data?.errors,
              });
            }
            reject({ status });
          })
          .finally(() => {
            dispatch({
              type: SUBMIT_COMPLETED
            });
          });
        return;
      }

      dispatch({
        type: SUBMIT_PRACTICE_INFO,
        errors: {
          practiceName: 'Practice name is required.',
          practiceEmail: 'Practice email is required.',
          practiceType: 'Please select a practice type.',
          practicePhone: 'Practice phone number is required.',
          howDidYouHearAboutUs: HOW_DID_YOU_HEAR_ABOUT_US_REQUIRED
        }
      });
      return reject({
        hasErrors: true
      });
    });
  };
};

export const CONFIRM_PASSWORD = 'CONFIRM_PASSWORD';
export const checkMatchingPassword = (current, confirmation) => ({
  type: CONFIRM_PASSWORD,
  errMsg: validatePasswordMatch(current, confirmation)
});

export const CREATE_ACCOUNT = 'CREATE_ACCOUNT';
export const CREATE_ACCOUNT_PROCESSING = 'CREATE_ACCOUNT_PROCESSING';
export const CREATE_ACCOUNT_COMPLETED = 'CREATE_ACCOUNT_COMPLETED';

export const createAccount = (data, registerFn = registerAccount) => {
  return (dispatch) => {
    return new Promise((resolve, reject) => {
      const errors = {};
      const emailMsg = validateEmail(data.email);

      if (emailMsg) {
        errors.practiceEmail = emailMsg;
      }

      const passwordMsg = validatePassword(data.password);
      if (passwordMsg) {
        errors.password = passwordMsg;
      }

      const passwordMatchMsg = validatePasswordMatch(data.password, data.confirmPassword);

      if (passwordMatchMsg) {
        errors.passwordConfirm = passwordMatchMsg;
      }

      if (errors.practiceEmail || errors.password || errors.passwordConfirm) {
        reject({
          hasError: true
        });
        return dispatch({
          type: CREATE_ACCOUNT,
          errors
        });
      }

      dispatch({
        type: PROCESSING_SUBMIT
      });

      return registerFn(data)
        .then((payload) => {
          dispatch({
            type: UPDATE_PROGRESS_BAR,
            payload
          });
          resolve();
        })
        .catch(({
          response
        }) => {
          const errors = response && !_isEmpty(response.data) || (response.data && !_isEmpty(response.data.errors))
            ? { ...response.data.errors }
            : { systemError: 'System error has occured please try again later.' };

          dispatch({
            type: CREATE_ACCOUNT,
            errors
          });

          const status = response && response.status;
          reject({ status });
        })
        .finally(() => {
          dispatch({
            type: SUBMIT_COMPLETED
          });
        });
    });
  };
};

export const ENTER_FIELD_DATA = 'ENTER_FIELD_DATA';
export const enterFieldData = (fieldName, value, validatonConfig = {
  isRequired: false
}) => {
  const {
    isRequired,
    fieldDisplayName,
    validator
  } = validatonConfig;

  if (isRequired && !value) {
    return {
      type: ENTER_FIELD_DATA,
      fieldName,
      value,
      errMsg: `${fieldDisplayName || fieldName} is required.`
    };
  }

  const action = {
    type: ENTER_FIELD_DATA,
    fieldName,
    value
  };

  const errMsg = validator && typeof validator === 'function' && validator(value);

  if (errMsg) {
    action.errMsg = errMsg;
  }

  return action;
};

const isValidDoctorEmail = (value, primaryEmail) => {
  if (value && primaryEmail && value === primaryEmail) {
    return () => 'Doctor email cannot be the same as login email.'
  }
  return validators.email({ message: 'A valid email address is required.'})
}

export const enterDoctorEmail = (value, primaryEmail, runValidation = true) => {
  const val = typeof value === 'string' && value.trim();
  
  return enterFieldData('doctorEmail', val, {
    validator: runValidation
      ? isValidDoctorEmail(value, primaryEmail)
      : () => {}
  });
};


export const SUBMIT_CONTACT_INFO = 'SUBMIT_CONTACT_INFO';
export const SUBMIT_CONTACT_INFO_PROCESSING = 'SUBMIT_CONTACT_INFO_PROCESSING';
export const SUBMIT_CONTACT_INFO_COMPLETED = 'SUBMIT_CONTACT_INFO_COMPLETED';

export const submitContactInfo = (data, fieldsToValidate, submitFn = submitContacts) => {
  return (dispatch) => (new Promise((resolve, reject) => {
    if (fieldsToValidate) {
      const requiredErrors = fieldsToValidate.required.reduce((errs, field) => {
        const {
          name,
          displayName,
          validator
        } = field;
        if (validator && typeof validator === 'function') {
          const message = validator(data[field.name], displayName);
          errs[name] = message;
          return errs;
        }

        const fieldName = field.displayName || field.name;
        let dataField = name === 'practicePhone' ? 'phone' : name;
        errs[name] = !data[dataField] ? `${fieldName} is required.` : null;
        return errs;
      }, {});

      const optionalErrors = fieldsToValidate.optional.reduce((errs, field) => {
        const displayName = field.displayName || field.name;
        const {
          validator
        } = field;
        errs[field.name] = data[field.name] && validator && typeof validator === 'function' ?
          validator(data[field.name], displayName) :
          null;

        return errs;
      }, {});

      const errors = {
        ...requiredErrors,
        ...optionalErrors
      };

      if (data.phone) {
        errors.phone = validatePhone(data.phone);
      }

      const hasErrors = Object.keys(errors)
        .reduce((curr, key) => (!!errors[key] || curr), false);

      if (hasErrors) {
        const action = {
          type: SUBMIT_CONTACT_INFO,
          errors
        };

        dispatch(action);
        return reject(new Error('validations failed for contact information'));
      }
    }

    dispatch({
      type: PROCESSING_SUBMIT
    });

    submitFn(data)
      .then((payload) => {
        if (payload && payload.errors && payload.errors.length === 1) {
          const [ error ] = payload.errors;
          dispatch({
            type: SUBMIT_CONTACT_INFO,
            errors: error
          });
          resolve({ hasError: true });
        }

        dispatch({
          type: UPDATE_PROGRESS_BAR,
          payload
        });
        resolve(payload);
      })
      .catch(reject)
      .finally(() => {
        dispatch({
          type: SUBMIT_COMPLETED
        });
      });
  }));
};

/* *********************************** Practice Info Page *********************************** */

const PRACTICE_LEGALNAME_REQUIRED = 'Legal Name is required';
const PRACTICE_BUSINESS_TIN_REQUIRED = 'Business TIN is required';

export const SUBMIT_BUSINESS_INFO = 'SUBMIT_BUSINESS_INFO';
export const SUBMIT_BUSINESS_INFO_COMPLETED = 'SUBMIT_BUSINESS_INFO_COMPLETED';

const submitBusinessData = (body) => {
  body.variation = 'ALT';
  return post('/patientsolutions/enroll/practice-information', body);
};

export const submitBusinessInfo = (data, submitFn = submitBusinessData) => {

  return (dispatch) => {
    return new Promise((resolve, reject) => {
      if (data) {
        const errors = {};

        if (!data.name) {
          errors.practiceName = PRACTICE_NAME_REQUIRED;
        }

        if (!data.type) {
          errors.practiceType = PRACTICE_TYPE_REQUIRED;
        }

        const streetErrMsg = validateStreet(data.street);
        if (streetErrMsg) {
          errors.street = streetErrMsg;
        }

        const aptsuiteErrMsg = validateAptSuite(data.aptsuite);
        if (aptsuiteErrMsg) {
          errors.aptsuite = aptsuiteErrMsg;
        }

        const cityErrMsg = validateCity(data.city);
        if (cityErrMsg) {
          errors.city = cityErrMsg;
        }

        const stateErrMsg = validateState(data.state);
        if (stateErrMsg) {
          errors.state = stateErrMsg;
        }

        const zipErrMsg = validateZip(data.zip);
        if (zipErrMsg) {
          errors.zip = zipErrMsg;
        }

        if (!data.legalName) {
          errors.legalName = PRACTICE_LEGALNAME_REQUIRED;
        }

        if (!data.businessTIN) {
          errors.businessTIN = PRACTICE_BUSINESS_TIN_REQUIRED;
        } else if (data.businessTIN.length !== 9) {
          errors.businessTIN = 'Business TIN should have 9 digits.';
        }

        if (!data.businessType) {
          errors.businessType = 'Business type is required.';
        }

        const hasErrors = Object.keys(errors)
          .reduce((curr, key) => !!errors[key] || curr, false);

        if (hasErrors) {
          const action = {
            type: SUBMIT_BUSINESS_INFO,
            errors
          };

          dispatch(action);
          return reject(new Error('validations failed for business information'));
        }
      }

      dispatch({
        type: PROCESSING_SUBMIT
      });

      submitFn(data)
        .then((payload) => {
          dispatch({
            type: UPDATE_PROGRESS_BAR,
            payload
          });
          resolve(payload);
        })
        .catch((error) => reject(error))
        .finally(() => {
          dispatch({
            type: SUBMIT_COMPLETED
          });
        });
    });
  };
};

/* *********************************** Payment Plans Page *********************************** */

const PAYMENT_PLANS_REQUIRED = 'Please select a Promotional No Interest plan. ';

export const SELECT_PAYMENT_PLANS = 'SELECT_PAYMENT_PLANS';

const submitPaymentPlansData = (body) => {
  return http.post('/patientsolutions/enroll/payment-plans', body)
    .then(({ data }) => (data));
};

export const submitPaymentPlans = (data, submitFn = submitPaymentPlansData) => {

  return (dispatch) => {
    return new Promise((resolve, reject) => {
      const { maxPNITerm } = data;

      const action = {
        type: SELECT_PAYMENT_PLANS,
        value: maxPNITerm
      };

      if (data.isMaxPNIValidationRequired && maxPNITerm < 0) {
        action.errMsg = PAYMENT_PLANS_REQUIRED;
        reject(new Error('validations failed for payment plans'));
        return;
      }

      dispatch({ type: PROCESSING_SUBMIT });

      submitFn({ maxPNITerm })
        .then((payload) => {
          dispatch({
            type: UPDATE_PROGRESS_BAR,
            payload
          });
          resolve(payload);
        })
        .catch(reject)
        .finally(() => {
          dispatch(action);
          dispatch({
            type: SUBMIT_COMPLETED
          });
        });
    });
  };
};

/* *********************************** Ach Info Page *********************************** */

const ACCOUNT_HOLDER_NAME_REQUIRED = 'Account Holder Name is required.';
const ROUTING_NUMBER_REQUIRED = 'Routing Number is required.';
const INVALID_ROUTING_NUMBER = 'Routing Number is invalid.';
const ACCOUNT_NUMBER_REQUIRED = 'Account Number is required.';

export const ENTER_ROUTING_NUMBER = 'ENTER_ROUTING_NUMBER';
export const CONFIRM_ACCOUNT_NUMBER = 'CONFIRM_ACCOUNT_NUMBER';
export const SUBMIT_ACH_INFO = 'SUBMIT_ACH_INFO';
export const CLEAN_ROUTING_ERRORS = 'CLEAN_ROUTING_ERRORS';

const getBankInfoFromRoutingNumber = (routingNumber) => {
  return http.post('/patientsolutions/enroll/ach-information/routing', {
    routingNumber,
    variation: 'ALT'
  });
};

const validateRoutingNumber = (routingNumber) => {
  const validator = validators.validABAChecksum({
    message: INVALID_ROUTING_NUMBER
  });
  return validator(routingNumber);
};

export const saveRoutingNumber = (routingNumber) => {
  return (dispatch) => {
    const type = ENTER_ROUTING_NUMBER;
    const achInfo = { routingNumber, bankName: null };
    const action = {
      type,
      achInfo
    };
    dispatch(action);
  };
};

export const saveRoutingNumberError = (routingNumber) => {
  return (dispatch) => {
    const type = ENTER_ROUTING_NUMBER;
    const errors = {};
    if (!routingNumber) {
      errors.routingNumber = ROUTING_NUMBER_REQUIRED;
    } else {
      errors.routingNumber = validateRoutingNumber(routingNumber);
    }
    const action = {
      type,
      errors
    };
    dispatch(action);
  };
};

export const enterRoutingNumber = (routingNumber, dispatchSpinnerAction, getFn = getBankInfoFromRoutingNumber) => {
  return (dispatch) => {
    return new Promise((resolve, reject) => {
      if (dispatchSpinnerAction) {
        dispatch({
          type: PROCESSING_SUBMIT
        });
      }
      const type = ENTER_ROUTING_NUMBER;
      let action = {
        type
      };
      let achInfo = { routingNumber, bankName: null },
        errors = {};

      action = {
        ...action,
        achInfo
      };
      dispatch(action);

      errors.routingNumber = validateRoutingNumber(routingNumber);
      if (errors.routingNumber) {
        action = {
          ...action,
          errors
        };

        dispatch(action);
        reject(new Error(INVALID_ROUTING_NUMBER));
      } else {
        dispatch({type: CLEAN_ROUTING_ERRORS});
        getFn(routingNumber)
          .then((response) => {
            const {data: bankName} = response;
            if (!bankName || bankName.errors) {
              errors.routingNumber = INVALID_ROUTING_NUMBER;
              action = {
                ...action,
                errors
              };
              dispatch(action);
              reject(new Error(INVALID_ROUTING_NUMBER));
            } else {
              achInfo = {
                ...bankName,
                routingNumber
              };

              action = {
                ...action,
                achInfo
              };
              dispatch(action);
              resolve(bankName);
            }
          })
          .catch((error) => {
            errors.routingNumber = 'Bank information cannot be obtained.';
            action = {
              ...action,
              errors
            };
            dispatch(action);
            reject(new Error(errors.routingNumber));
          })
          .finally(() => {
            if (dispatchSpinnerAction) {
              dispatch({
                type: SUBMIT_COMPLETED
              });
            }
          });
        }
    });
  };
};

const validateAccountNumberMatch = (val, val2) => val !== val2 && 'Please enter same account number as above.';

export const checkMatchingAccountNumber = (current, confirmation) => {
  const errMsg = validateAccountNumberMatch(current, confirmation);
  if (errMsg) {
    return {
      type: CONFIRM_ACCOUNT_NUMBER,
      errMsg
    };
  }
  return {
    type: CONFIRM_ACCOUNT_NUMBER,
    value: confirmation
  };
};

const submitAchData = (data) => {
  data.variation = 'ALT';
  return post('/patientsolutions/enroll/ach-information', data);
};

export const submitAchInfo = (data, submitFn = submitAchData) => {

  return (dispatch) => {
    return new Promise((resolve, reject) => {
      const errors = {};
      const {
        achRoutingNumber: routingNumber,
        achAccountName,
        achAccountNumber,
        achBankName,
        achConfirmAccountNumber
      } = data;

      if (!achAccountName) {
        errors.achAccountName = ACCOUNT_HOLDER_NAME_REQUIRED;
      } else {
        errors.achAccountName = validateName('Account Holder Name')(achAccountName);
      }

      if (!routingNumber) {
        errors.achRoutingNumber = ROUTING_NUMBER_REQUIRED;
      } else {
        errors.achRoutingNumber = validateRoutingNumber(routingNumber);
      }

      if (!errors.achRoutingNumber && !achBankName) {
        errors.achRoutingNumber = INVALID_ROUTING_NUMBER;
      }

      if (!achAccountNumber) {
        errors.achAccountNumber = ACCOUNT_NUMBER_REQUIRED;
      } else {
        errors.achConfirmAccountNumber = validateAccountNumberMatch(achAccountNumber, achConfirmAccountNumber);
      }

      const hasErrors = Object.keys(errors)
        .reduce((curr, key) => (errors[key] || curr), false);

      if (hasErrors) {
        const action = {
          type: SUBMIT_ACH_INFO,
          errors
        };

        dispatch(action);
        return reject(new Error('validations failed for ach information'));
      }

      dispatch({
        type: PROCESSING_SUBMIT
      });

      const type = ENTER_ROUTING_NUMBER;
      let action = {
        type
      };

      getBankInfoFromRoutingNumber(routingNumber)
        .then((response) => {
          const {data: bankName} = response;
          if (!bankName || bankName.errors) {
            errors.routingNumber = INVALID_ROUTING_NUMBER;
            action = {
              ...action,
              errors
            };

            dispatch(action);
            throw new Error(INVALID_ROUTING_NUMBER);
          } else {
            const achInfo = {
              ...bankName,
              routingNumber
            };

            action = {
              ...action,
              achInfo
            };
            dispatch(action);
            return {
              achAccountName,
              achAccountNumber,
              achBankName: bankName.bankName,
              achRoutingNumber: routingNumber
            };
          }
        })
        .then(submitFn)
        .then((payload) => {
          dispatch({
            type: UPDATE_PROGRESS_BAR,
            payload
          });
          resolve(payload);
        })
        .catch((error) => {
          if (error.message === INVALID_ROUTING_NUMBER) {
            errors.bankName = 'Bank information cannot be obtained.';
            action = {
              ...action,
              errors
            };
            dispatch(action);
          }
          return reject(error);
        })
        .finally(() => {
          dispatch({
            type: SUBMIT_COMPLETED
          });
        });
    });
  };
};

/* *********************************** Agreement Page *********************************** */
const SIGNEE_FIRSTNAME_REQUIRED = 'Signee First Name is required.';
const SIGNEE_LASTNAME_REQUIRED = 'Signee Last Name is required.';
const SIGNEE_TITLE_REQUIRED = 'Signee Title is required.';

export const SIGN_AGREEMENT = 'SIGN_AGREEMENT';

const submitSignAgreementData = (body) => {
  body.variation = 'ALT';
  return post('/patientsolutions/enroll/agreement', body);
};

export const signAgreement = (data, hasConsent, submitFn = submitSignAgreementData) => {
  return (dispatch) => {
    return new Promise((resolve, reject) => {
      if (data) {
        const errors = {};

        if (!data.signeeFirstName) {
          errors.signeeFirstName = SIGNEE_FIRSTNAME_REQUIRED;
        } else {
          errors.signeeFirstName = validateName('Signee First Name')(data.signeeFirstName);
        }

        if (!data.signeeLastName) {
          errors.signeeLastName = SIGNEE_LASTNAME_REQUIRED;
        } else {
          errors.signeeLastName = validateName('Signee Last Name')(data.signeeLastName);
        }

        if (!data.signeeTitle) {
          errors.signeeTitle = SIGNEE_TITLE_REQUIRED;
        } else {
          errors.signeeTitle = validateName('Signee Title')(data.signeeTitle);
        }

        errors.hasConsent = !hasConsent;

        const hasErrors = Object.keys(errors)
          .reduce((curr, key) => (errors[key] || curr), false);

        if (hasErrors) {
          const action = {
            type: SIGN_AGREEMENT,
            errors
          };

          dispatch(action);
          return reject(new Error('validations failed for sign agreement'));
        }

        dispatch({
          type: PROCESSING_SUBMIT
        });

        submitFn(data)
          .then((payload) => {
            dispatch({
              type: UPDATE_PROGRESS_BAR,
              payload
            });
            resolve(payload);
          })
          .catch(reject)
          .finally(() => {
            dispatch({
              type: SUBMIT_COMPLETED
            });
          });
      }
    });
  };
};

const setController = (index) => {
  return {
    type: 'SELECT_CONTROLLER',
    index
  };
};

export default {
  enterPracticeName,
  enterPracticeEmail,
  enterPracticePhone,
  selectPracticeType,
  selectHowDidYouHearAboutUs,
  submitPracticeInfo,
  validatePassword,
  createAccount,
  checkMatchingPassword,
  enterFieldData,
  enterDoctorEmail,
  submitContactInfo,
  enterStreetAddress,
  validateStreet,
  enterAptSuite,
  validateAptSuite,
  enterCity,
  validateCity,
  selectState,
  validateState,
  enterZip,
  validateZip,
  submitBusinessInfo,
  submitOwnershipInfo,
  submitOwnershipFlag,
  addOwner,
  updateOwner,
  removeOwner,
  setPreviousRoute,
  updateControllerField,
  updateManagementField,
  submitPaymentPlans,
  saveRoutingNumber,
  saveRoutingNumberError,
  enterRoutingNumber,
  checkMatchingAccountNumber,
  submitAchInfo,
  setController,
  updateManagementInfo,
  hasIncompleteOwner,
  signAgreement,
  getApplication,
  submitConfirmation,
  validatePasswordField
};

